import {
  hasInterestRateChart,
  refusesUpfrontInterest,
  toDepositTypeId,
} from "../../../../helpers/depositType";
import { validateChart } from "../../../../helpers/interestRateChart";
import {
  TCreateInvestmentProductState,
  TInvestmentProductDetails,
} from "../CreateInvestmentProduct";
import { needsWithdrawalWindows, PlanType } from "./planType";
import { WINDOW_UNIT_NAME, WindowUnit } from "./withdrawalWindows";

/**
 * What a fund still needs before it can be created.
 *
 * These are the rules creation is judged by, checked here so they are met on
 * screen rather than as a 400 with a server message. Each one names the
 * section it belongs to, so the review can offer to take the admin there.
 *
 * Worded for whoever is filling the form in. Nothing here mentions payloads,
 * endpoints or which side of the wire enforces what — none of that is theirs
 * to care about.
 */

export type TReviewProblem = {
  /** The section id, so the review can scroll to it. */
  section: string;
  /** Where it lives, as the admin reads it. */
  where: string;
  message: string;
};

const MAX_INTERVAL: Record<number, number> = {
  [WindowUnit.Daily]: 365,
  [WindowUnit.Weekly]: 52,
  [WindowUnit.Monthly]: 12,
};

/** `Enums.InterestRepaymentType.Upfront` — the payment type's id. */
const UPFRONT_INTEREST_PAYMENT_TYPE_ID = 3;

const MIN_RATE = 0;
const MAX_RATE = 100;

export function reviewFund(
  state: TCreateInvestmentProductState,
): TReviewProblem[] {
  return [
    ...reviewDetails(state),
    ...reviewWindows(state),
    ...state.investmentProductDetails.flatMap(reviewCurrency),
  ];
}

function reviewDetails(state: TCreateInvestmentProductState): TReviewProblem[] {
  const problems: TReviewProblem[] = [];
  const at = (message: string) =>
    problems.push({
      section: "investment-details",
      where: "Investment details",
      message,
    });

  if (!state.name?.trim()) {
    at("The fund has no name");
  }

  if (!state.investmentProductTypeId) {
    at("Investment type is not set");
  }

  if (!state.startDate) {
    at("Subscription opening date is not set");
  }

  if (!state.endDate) {
    at("Subscription closing date is not set");
  }

  if (
    state.startDate &&
    state.endDate &&
    new Date(state.endDate) < new Date(state.startDate)
  ) {
    at("Subscriptions close before they open");
  }

  return problems;
}

function reviewWindows(state: TCreateInvestmentProductState): TReviewProblem[] {
  if (!needsWithdrawalWindows(state.productTypeId)) {
    return [];
  }

  const problems: TReviewProblem[] = [];
  const at = (message: string) =>
    problems.push({
      section: "withdrawal-windows",
      where: "Withdrawal windows",
      message,
    });

  const unit = state.withdrawalWindowIntervalUnit ?? WindowUnit.Monthly;
  const interval = state.withdrawalWindowInterval;
  const max = MAX_INTERVAL[unit];

  if (!interval || interval <= 0) {
    at("How often a window opens is not set");
  } else if (max && interval > max) {
    at(`A window cannot open more than ${max} ${WINDOW_UNIT_NAME[unit]} apart`);
  }

  if (
    state.gracePeriod === null ||
    state.gracePeriod === undefined ||
    state.gracePeriod < 0
  ) {
    at("How long a window stays open is not set");
  }

  if (
    state.lockInPeriod !== null &&
    state.lockInPeriod !== undefined &&
    !(state.lockInPeriod > 0)
  ) {
    at("A lock-in period must be at least 1 day");
  }

  return problems;
}

function reviewCurrency(detail: TInvestmentProductDetails): TReviewProblem[] {
  const problems: TReviewProblem[] = [];
  const currency = detail?.currencyCode || detail?.value || "this currency";
  const where = `Financials · ${detail?.label || currency}`;
  const at = (message: string) =>
    problems.push({ section: "financials", where, message });

  if (!detail?.coreBankingTenantId) {
    at("Core banking tenant is not set");
  }

  if (!detail?.coreBankingProductId) {
    at("Core banking product is not set");
  }

  if (!toDepositTypeId(detail?.depositType)) {
    at("Deposit type is not set");
  }

  const min = Number(detail?.minimumInvestment);
  const max = Number(detail?.maximumInvestment);

  if (!min || min <= 0) {
    at("Minimum investment is not set");
  }

  if (!max || max <= 0) {
    at("Maximum investment is not set");
  }

  if (min > 0 && max > 0 && max < min) {
    at("Maximum investment is below the minimum");
  }

  reviewLiquidationRate(at, detail?.earlyLiquidationChargePercentage);

  if (!detail?.investmentInterestPaymentTypeId) {
    at("Interest payment type is not set");
  } else if (
    Number(detail.investmentInterestPaymentTypeId) ===
      UPFRONT_INTEREST_PAYMENT_TYPE_ID &&
    refusesUpfrontInterest(detail?.depositType)
  ) {
    at("Recurring and dynamic deposits can't pay interest upfront");
  }

  if (!detail?.termsAndConditionId) {
    at("Terms and conditions is not set");
  }

  if (hasInterestRateChart(detail?.depositType)) {
    const chart = detail?.interestRateChart;

    if (!chart || !chart.chartSlabs?.length) {
      at("The rate chart has no bands");
    } else {
      // The chart's own rules, already stated once. Reported here so the
      // review is the single place an admin looks.
      validateChart(chart).forEach((error) =>
        at(`Rate chart: ${error.message}`),
      );
    }
  }

  return problems;
}

/**
 * The early liquidation rate, which is a percentage.
 *
 * Optional: left empty, early withdrawal is free. Zero is also a real answer,
 * so only a value outside the range is a problem.
 */
function reviewLiquidationRate(
  at: (message: string) => void,
  rate: number | null | undefined,
) {
  if (rate === undefined || rate === null || Number.isNaN(Number(rate))) {
    return;
  }

  if (Number(rate) < MIN_RATE || Number(rate) > MAX_RATE) {
    at(`Early liquidation rate must be between ${MIN_RATE} and ${MAX_RATE}`);
  }
}

/** True when the fund carries a savings plan type at all. */
export function isSavingsPlan(planTypeId?: number | null): boolean {
  return Object.values(PlanType).includes(Number(planTypeId) as any);
}
